import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Send, Clock, Trash2, ChevronDown, ChevronUp,
  RefreshCw, FileText, TrendingUp, AlertTriangle, CheckCircle2, History,
} from "lucide-react";

const STATUS_COLOR = {
  good: "text-emerald-600 bg-emerald-50",
  warning: "text-amber-600 bg-amber-50",
  critical: "text-red-600 bg-red-50",
};

export default function AIReportGenerator() {
  const { api } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchSuggestions();
    fetchHistory();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const res = await api.get("/compliance-reports/suggestions");
      setSuggestions(res.data.suggestions || []);
    } catch { /* silent */ }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/compliance-reports/history?report_type=custom_ai");
      setHistory(res.data.reports || []);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || prompt.trim().length < 5) { toast.error("Please enter a more detailed query"); return; }
    setGenerating(true);
    setReport(null);
    try {
      const res = await api.post("/compliance-reports/ai-generate", { prompt: prompt.trim() });
      setReport(res.data);
      toast.success("Report generated successfully");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
    } finally { setGenerating(false); }
  };

  const loadReport = async (reportId) => {
    try {
      const res = await api.get(`/compliance-reports/history/${reportId}`);
      const r = res.data.report;
      setReport({ data: r.data, ai_source: r.ai_source, title: r.title });
      setPrompt(r.prompt || "");
    } catch { toast.error("Failed to load report"); }
  };

  const deleteReport = async (reportId) => {
    try {
      await api.delete(`/compliance-reports/history/${reportId}`);
      toast.success("Report deleted");
      fetchHistory();
    } catch { toast.error("Failed to delete"); }
  };

  const applySuggestion = (text) => {
    setPrompt(text);
    textareaRef.current?.focus();
  };

  const reportData = report?.data || null;

  return (
    <div className="space-y-6" data-testid="ai-report-generator">
      {/* Prompt Input */}
      <Card className="border-2 border-violet-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">AI-Powered Report Generator</CardTitle>
              <CardDescription>Describe the report you need — AI will query all modules and generate insights from live data</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Generate an executive summary of our BCM program readiness..."
              className="min-h-[80px] resize-none border-violet-200 focus:border-violet-400"
              data-testid="report-prompt-input"
              onKeyDown={(e) => { if (e.key === "Enter" && e.metaKey) handleGenerate(); }}
            />

            {/* AI-Guided Suggestions */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">AI-Suggested Prompts</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => applySuggestion(s.prompt)}
                    className="px-2.5 py-1 text-xs bg-violet-50 text-violet-700 rounded-full border border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors"
                    data-testid={`suggestion-${i}`}
                  >
                    {s.prompt.length > 55 ? s.prompt.substring(0, 55) + "..." : s.prompt}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">Cmd+Enter to generate</p>
              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="generate-report-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {generating ? "Generating Report..." : "Generate Report"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Result */}
      {reportData && (
        <div className="space-y-4" data-testid="report-result">
          {/* Title & Summary */}
          <Card className="border-l-4 border-l-violet-500">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{reportData.title || "Generated Report"}</CardTitle>
                <Badge className="bg-violet-100 text-violet-700 text-xs">{report?.ai_source || "AI"}</Badge>
              </div>
              {reportData.summary && (
                <CardDescription className="mt-1 text-sm">{reportData.summary}</CardDescription>
              )}
            </CardHeader>
          </Card>

          {/* Key Metrics */}
          {reportData.key_metrics && reportData.key_metrics.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {reportData.key_metrics.map((m, i) => {
                const colorClass = STATUS_COLOR[m.status] || "text-slate-600 bg-slate-50";
                return (
                  <Card key={i} className={`${colorClass.split(" ")[1]} border-0`}>
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-slate-500">{m.label}</p>
                      <p className={`text-xl font-bold mt-1 ${colorClass.split(" ")[0]}`}>{m.value}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Sections */}
          {reportData.sections && reportData.sections.map((section, si) => (
            <Card key={si}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{section.heading}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.content && (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{section.content}</p>
                )}

                {section.data_table && section.data_table.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          {Object.keys(section.data_table[0]).map(k => (
                            <th key={k} className="px-3 py-2 text-left font-semibold text-slate-700 border-b">{k}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.data_table.map((row, ri) => (
                          <tr key={ri} className="border-b border-slate-100">
                            {Object.values(row).map((v, vi) => (
                              <td key={vi} className="px-3 py-2 text-slate-600">{String(v)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section.findings && section.findings.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Key Findings</p>
                    <ul className="space-y-1">
                      {section.findings.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {section.recommendations && section.recommendations.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Recommendations</p>
                    <ul className="space-y-1">
                      {section.recommendations.map((r, ri) => (
                        <li key={ri} className="flex items-start gap-2 text-sm text-indigo-700">
                          <TrendingUp className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Overall Assessment */}
          {reportData.overall_assessment && (
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Overall Assessment</p>
                <p className="text-sm text-slate-800 leading-relaxed">{reportData.overall_assessment}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Report History */}
      <Card>
        <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowHistory(!showHistory)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-base">Report History ({history.length})</CardTitle>
            </div>
            {showHistory ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {showHistory && (
          <CardContent className="pt-0">
            {historyLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-violet-600 mx-auto" />
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No report history yet</p>
            ) : (
              <div className="space-y-2">
                {history.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-violet-50 transition-colors">
                    <div className="flex-1 cursor-pointer" onClick={() => loadReport(r.id)}>
                      <p className="text-sm font-medium text-slate-800">{r.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</p>
                        <Badge className="text-xs bg-violet-100 text-violet-600">{r.ai_source}</Badge>
                      </div>
                      {r.prompt && <p className="text-xs text-slate-400 mt-1 truncate max-w-md">{r.prompt}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => loadReport(r.id)} data-testid={`load-report-${r.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => deleteReport(r.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
